import type {
  CollaborationPresenceTargetType,
  CollaborationPresenceUser,
} from '@/stores/collaborationPresenceStore';
import type { ContentType, StoryboardItem } from '@/types/preview';

const RESOURCE_TAG_CONTENT_TYPES: ContentType[] = ['character', 'location', 'prop'];

export interface CollaborationSelectionTarget {
  type: CollaborationPresenceTargetType;
  id: string;
  storyboardId?: string | null;
  resourceType?: string | null;
}

export interface CollaborationPresenceColor {
  hex: string;
  softHex: string;
}

const BLUE_HUE_START = 180;
const BLUE_HUE_END = 250;
const BLUE_HUE_SKIP_OFFSET = 90;

/**
 * 协作在线成员调色板：排除蓝色系，避免与本机选中态的蓝色高亮混淆。
 * 人数超出调色板时由 createHslPresenceColor 用黄金角均匀打散兜底。
 */
const PRESENCE_COLOR_PALETTE: CollaborationPresenceColor[] = [
  { hex: '#d14d61', softHex: 'rgba(209, 77, 97, 0.18)' }, // 红
  { hex: '#cc8629', softHex: 'rgba(204, 134, 41, 0.18)' }, // 橙
  { hex: '#f59e0b', softHex: 'rgba(245, 158, 11, 0.2)' }, // 琥珀
  { hex: '#10b981', softHex: 'rgba(16, 185, 129, 0.18)' }, // 黄绿
  { hex: '#3da376', softHex: 'rgba(61, 163, 118, 0.18)' }, // 绿
  { hex: '#8b5cf6', softHex: 'rgba(139, 92, 246, 0.18)' }, // 紫
  { hex: '#7c5ba8', softHex: 'rgba(124, 91, 168, 0.18)' }, // 紫
  { hex: '#db2777', softHex: 'rgba(219, 39, 119, 0.18)' }, // 品红
];

/**
 * 多人协作专属色：在线成员 > 1 时用于选区边框，区别于个人调色板。
 * 选用品红紫（#c026d3），与彩虹个人色存在明显色相间隔，便于一眼识别为「多人」。
 */
export const MULTI_USER_PRESENCE_COLOR: CollaborationPresenceColor = {
  hex: '#7c5ba8',
  softHex: 'rgba(124, 91, 168, 0.18)',
};

function hashUserId(userId: string): number {
  let hash = 0;
  for (let index = 0; index < userId.length; index += 1) {
    hash = (hash * 31 + userId.charCodeAt(index)) >>> 0;
  }
  return hash;
}

export function getCollaborationPresenceColor(userId: string): CollaborationPresenceColor {
  const index = hashUserId(userId) % PRESENCE_COLOR_PALETTE.length;
  return PRESENCE_COLOR_PALETTE[index] ?? PRESENCE_COLOR_PALETTE[0];
}

/** 调色板耗尽后的兜底色：黄金角散列色相，跳过蓝色系以保证大量在线用户仍可区分 */
function createHslPresenceColor(overflowIndex: number): CollaborationPresenceColor {
  const rawHue = Math.round((overflowIndex * 137.508) % 360);
  const hue =
    rawHue >= BLUE_HUE_START && rawHue <= BLUE_HUE_END
      ? (rawHue + BLUE_HUE_SKIP_OFFSET) % 360
      : rawHue;
  return {
    hex: `hsl(${hue}, 68%, 55%)`,
    softHex: `hsla(${hue}, 68%, 55%, 0.18)`,
  };
}

/**
 * 基于当前在线成员有序列表，生成 userId → 颜色 的唯一映射。
 * 策略：哈希优先落位，槽位被占用则线性探测下一个空闲色，保证同一在线集合内颜色互不重复；
 * 颜色尽量稳定（仅碰撞时顺延），调色板用尽后用 HSL 兜底。
 * 入参应为 store 已稳定排序的在线用户列表，以保证多端分配结果一致。
 */
export function buildPresenceColorMap(
  users: Pick<CollaborationPresenceUser, 'userId'>[]
): Map<string, CollaborationPresenceColor> {
  const map = new Map<string, CollaborationPresenceColor>();
  const usedSlots = new Set<number>();
  const paletteSize = PRESENCE_COLOR_PALETTE.length;
  let assignedCount = 0;

  for (const user of users) {
    if (!user.userId || map.has(user.userId)) continue;

    if (usedSlots.size < paletteSize) {
      let index = hashUserId(user.userId) % paletteSize;
      let attempts = 0;
      while (usedSlots.has(index) && attempts < paletteSize) {
        index = (index + 1) % paletteSize;
        attempts += 1;
      }
      usedSlots.add(index);
      map.set(user.userId, PRESENCE_COLOR_PALETTE[index] ?? PRESENCE_COLOR_PALETTE[0]);
    } else {
      map.set(user.userId, createHslPresenceColor(assignedCount - paletteSize));
    }
    assignedCount += 1;
  }

  return map;
}

/** displayName 已由后端按 nickname → username → phone 优先级组装 */
export function getCollaborationDisplayName(user: CollaborationPresenceUser): string {
  return user.displayName?.trim() || user.userId.slice(0, 8);
}

export function getCollaborationPresenceListName(user: CollaborationPresenceUser): string {
  const name = getCollaborationDisplayName(user);
  return user.orgKind === 'contractor' ? `${name}（承制方）` : name;
}

export function getCollaborationAvatarLabel(
  user: CollaborationPresenceUser,
  maxLength = 2
): string {
  const name = getCollaborationDisplayName(user);
  const trimmed = name.trim();
  if (!trimmed) return '?';
  return trimmed.slice(0, maxLength);
}

/** 同一目标上按选中时间先后排序，用于边框颜色与标签顺序 */
export function isStoryboardSchemePreviewItem(
  item: Pick<StoryboardItem, 'id' | 'tagId'>,
  contentType?: ContentType
): boolean {
  return contentType === 'storyboard' && Boolean(item.tagId) && item.id === item.tagId;
}

/** 缩略图条：资源标签 / 分镜方案走 resource_tag，具体素材走 project_resource */
export function resolveThumbnailSelectionPresenceTarget(
  item: Pick<StoryboardItem, 'id' | 'tagId'>,
  contentType: ContentType,
  options?: { storyboardId?: string | null }
): CollaborationSelectionTarget {
  const storyboardId = options?.storyboardId ?? null;
  if (item.tagId && RESOURCE_TAG_CONTENT_TYPES.includes(contentType)) {
    return { type: 'resource_tag', id: item.tagId };
  }
  if (isStoryboardSchemePreviewItem(item, contentType)) {
    return { type: 'resource_tag', id: item.tagId!, storyboardId };
  }
  if (contentType === 'video') {
    return resolveStoryboardVideoSelectionPresenceTarget(item.id, { storyboardId });
  }
  return { type: 'project_resource', id: item.id, storyboardId };
}

export function collectResourceTagIds(source: {
  metadata?: unknown;
  tags?: Array<Record<string, unknown>>;
}): string[] {
  const metadata =
    typeof source.metadata === 'object' && source.metadata !== null
      ? (source.metadata as Record<string, unknown>)
      : {};
  const metadataTags = Array.isArray(metadata.tags)
    ? (metadata.tags as Array<Record<string, unknown>>)
    : [];
  const rootTags = Array.isArray(source.tags) ? source.tags : [];
  const ids = new Set<string>();
  for (const tag of [...metadataTags, ...rootTags]) {
    const tagId = String(tag.tagId ?? tag.tag_id ?? tag.id ?? '').trim();
    if (tagId) ids.add(tagId);
  }
  return [...ids];
}

export function resolveStoryboardImageSelectionPresenceTarget(
  imageId: string,
  options?: { storyboardId?: string | null }
): CollaborationSelectionTarget {
  return {
    type: 'project_resource',
    id: imageId,
    storyboardId: options?.storyboardId ?? null,
  };
}

export function resolveStoryboardSchemeSelectionPresenceTarget(
  planId: string,
  options?: { storyboardId?: string | null }
): CollaborationSelectionTarget {
  return {
    type: 'resource_tag',
    id: planId,
    storyboardId: options?.storyboardId ?? null,
  };
}

const STORYBOARD_ASSOCIATION_RESOURCE_TYPES = new Set(['character', 'location', 'prop', 'footage']);

const UUID_V7_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-7[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

function isUuid(value: string): boolean {
  return UUID_V7_PATTERN.test(value.trim());
}

export const STORYBOARD_VIDEO_VIRTUAL_ITEM_PREFIX = '__storyboard-video-virtual__';

/** 分镜视频 project_resource 目标（与分镜图片 project_resource 区分） */
export function isStoryboardVideoProjectResourceTarget(
  target: Pick<CollaborationSelectionTarget, 'type' | 'id' | 'resourceType'>
): boolean {
  if (target.type !== 'project_resource') return false;
  const id = String(target.id ?? '').trim();
  if (!id) return false;
  if (id.startsWith(STORYBOARD_VIDEO_VIRTUAL_ITEM_PREFIX)) return true;
  if (id === 'new-video-generation') return true;
  return String(target.resourceType ?? '').trim() === 'video';
}

export function resolveStoryboardVideoSelectionPresenceTarget(
  videoId: string,
  options?: { storyboardId?: string | null }
): CollaborationSelectionTarget {
  return {
    type: 'project_resource',
    id: videoId,
    storyboardId: options?.storyboardId ?? null,
    resourceType: 'video',
  };
}

/** 非真实成片 id（占位/虚拟/未生成） */
export function isNonAssetStoryboardVideoPreviewItemId(
  previewItemId: string | null | undefined
): boolean {
  const id = String(previewItemId ?? '').trim();
  if (!id) return true;
  if (id === 'new-video-generation') return true;
  return id.startsWith(STORYBOARD_VIDEO_VIRTUAL_ITEM_PREFIX);
}

/** 未生成视频的分镜视频轨占位：优先占位 id，而非 preview 里的虚拟/空 id */
export function shouldPreferVideoTrackPlaceholderPresenceTarget(params: {
  editorMode: string | null | undefined;
  previewItemId: string | null | undefined;
  selectedTagId: string | null | undefined;
  storyboardId: string | null | undefined;
  videoTrack?: ReturnType<typeof resolveVideoTrackBoardSelectionItem>;
}): boolean {
  const editorMode = String(params.editorMode ?? '').trim();
  const storyboardId = String(params.storyboardId ?? '').trim();
  if (editorMode !== 'storyboard-video' || !storyboardId) return false;
  if (params.videoTrack?.videoId) return false;

  const previewItemId = String(params.previewItemId ?? '').trim();
  const tagId = String(params.selectedTagId ?? '').trim();

  if (isNonAssetStoryboardVideoPreviewItemId(previewItemId)) return true;
  if (tagId && previewItemId === tagId) return true;

  return false;
}

/** 分镜视频轨占位块 remote 匹配 */
export function matchesVideoTrackPlaceholderRemoteSelection(
  target: Pick<CollaborationSelectionTarget, 'type' | 'id' | 'storyboardId' | 'resourceType'>,
  params: { boardElementId: string; storyboardId?: string | null }
): boolean {
  const normalizedBoardElementId = params.boardElementId.trim();
  if (!normalizedBoardElementId) return false;
  if (target.type === 'storyboard' && target.id === normalizedBoardElementId) return true;

  if (!isStoryboardVideoProjectResourceTarget(target)) return false;

  const normalizedStoryboardId = String(params.storyboardId ?? '').trim();
  const targetStoryboardId = String(target.storyboardId ?? '').trim();
  if (
    normalizedStoryboardId &&
    targetStoryboardId &&
    targetStoryboardId !== normalizedStoryboardId
  ) {
    return false;
  }

  const id = String(target.id ?? '').trim();
  return id === 'new-video-generation' || id.startsWith(STORYBOARD_VIDEO_VIRTUAL_ITEM_PREFIX);
}

function hasMatchingStoryboardContext(
  target: Pick<CollaborationSelectionTarget, 'storyboardId'>,
  storyboardId: string
): boolean {
  return String(target.storyboardId ?? '').trim() === storyboardId.trim();
}

/** 看板「资源关联」轨道块 element id，与 StoryboardBoardTrack 保持一致 */
export function buildStoryboardResourceTrackBoardElementId(storyboardId: string): string {
  return `resource-${storyboardId.trim()}`;
}

export type StoryboardBoardPlaceholderTrackType = 'video' | 'dialogue' | 'voice' | 'sound';

/** 汇总/占位轨道的 presence id（UI 元素 id 可能仍是裸 storyboardId） */
export function buildStoryboardTrackPlaceholderBoardElementId(
  storyboardId: string,
  trackType: StoryboardBoardPlaceholderTrackType
): string {
  return `${storyboardId.trim()}-placeholder-${trackType}`;
}

/** 是否为分镜图片轨道的 storyboard 选中（裸分镜 id，不含其他轨道后缀） */
export function isStoryboardImageTrackBoardSelectionTarget(
  target: Pick<CollaborationSelectionTarget, 'type' | 'id'>
): boolean {
  if (target.type !== 'storyboard') return false;
  const id = String(target.id ?? '').trim();
  if (!id || id.startsWith('resource-')) return false;
  if (id.includes('-placeholder-') || id.includes('-element-')) return false;
  return true;
}

/** 非分镜图片轨（视频/对白/配音/音效）的看板 presence 目标 */
export function resolveNonImageBoardTrackPresenceTarget(params: {
  editorMode: string | null | undefined;
  storyboardId: string | null | undefined;
  selectedStoryboardBoardItems?: readonly string[] | null;
}): CollaborationSelectionTarget | null {
  const storyboardId = String(params.storyboardId ?? '').trim();
  if (!storyboardId) return null;

  const editorMode = String(params.editorMode ?? '').trim();
  const trackTypeByMode: Partial<Record<string, StoryboardBoardPlaceholderTrackType>> = {
    'storyboard-video': 'video',
    'dialogue-edit': 'dialogue',
    'voice-generation': 'voice',
    'sound-effect': 'sound',
  };
  const placeholderTrackType = trackTypeByMode[editorMode];
  if (!placeholderTrackType) return null;

  const boardItems = params.selectedStoryboardBoardItems ?? [];
  const specificElementId = boardItems.find(
    (itemId) =>
      !itemId.startsWith('track-') &&
      itemId !== storyboardId &&
      (itemId.includes('-element-') || itemId.includes('-placeholder-'))
  );
  if (specificElementId) {
    return { type: 'storyboard', id: specificElementId };
  }

  return {
    type: 'storyboard',
    id: buildStoryboardTrackPlaceholderBoardElementId(storyboardId, placeholderTrackType),
  };
}

export function resolveBoardElementPresenceTargetId(params: {
  elementId: string;
  trackType?: 'storyboard' | 'video' | 'dialogue' | 'voice' | 'sound' | 'resource' | null;
  storyboardId?: string | null;
  isPlaceholder?: boolean;
}): string {
  const elementId = String(params.elementId ?? '').trim();
  const storyboardId = String(params.storyboardId ?? '').trim() || elementId;
  if (!params.isPlaceholder) return elementId;

  switch (params.trackType) {
    case 'video':
      return buildStoryboardTrackPlaceholderBoardElementId(storyboardId, 'video');
    case 'dialogue':
      return buildStoryboardTrackPlaceholderBoardElementId(storyboardId, 'dialogue');
    case 'voice':
      return buildStoryboardTrackPlaceholderBoardElementId(storyboardId, 'voice');
    case 'sound':
      return buildStoryboardTrackPlaceholderBoardElementId(storyboardId, 'sound');
    case 'resource':
      return buildStoryboardResourceTrackBoardElementId(storyboardId);
    default:
      return elementId;
  }
}

/** 分镜看板「资源关联」轨道内的选中（角色/场景/道具/实拍 tag 或 storyboard_item） */
export function isStoryboardResourceAssociationTarget(
  target: Pick<CollaborationSelectionTarget, 'type' | 'resourceType'>
): boolean {
  if (target.type === 'storyboard_item') return true;
  const resourceType = String(target.resourceType ?? '').trim();
  if (!STORYBOARD_ASSOCIATION_RESOURCE_TYPES.has(resourceType)) return false;
  return target.type === 'resource_element' || target.type === 'resource_tag';
}

/** 底部分镜看板「分镜图片」轨道块：分镜本身 + 方案/图片素材（不含视频） */
export function matchesStoryboardImageTrackRemoteSelection(
  target: Pick<CollaborationSelectionTarget, 'type' | 'id' | 'storyboardId' | 'resourceType'>,
  storyboardId: string
): boolean {
  const normalizedStoryboardId = storyboardId.trim();
  if (!normalizedStoryboardId) return false;

  if (
    target.type === 'storyboard' &&
    target.id === normalizedStoryboardId &&
    isStoryboardImageTrackBoardSelectionTarget(target)
  ) {
    return true;
  }

  if (!hasMatchingStoryboardContext(target, normalizedStoryboardId)) {
    return false;
  }

  if (target.type === 'project_resource' && !isStoryboardVideoProjectResourceTarget(target)) {
    return true;
  }
  if (target.type === 'resource_tag' && !isStoryboardResourceAssociationTarget(target)) {
    return true;
  }

  return false;
}

/** 底部分镜看板「资源关联」轨道块：仅整块选中（resource-${storyboardId}） */
export function matchesStoryboardResourceTrackRemoteSelection(
  target: Pick<CollaborationSelectionTarget, 'type' | 'id' | 'storyboardId' | 'resourceType'>,
  storyboardId: string
): boolean {
  const normalizedStoryboardId = storyboardId.trim();
  if (!normalizedStoryboardId) return false;

  return (
    target.type === 'storyboard' &&
    target.id === buildStoryboardResourceTrackBoardElementId(normalizedStoryboardId)
  );
}

/** 分镜级外层（左侧分镜卡等）：该分镜下资源关联条目/tag/元素选中 */
export function matchesStoryboardResourceAssociationRemoteSelection(
  target: Pick<CollaborationSelectionTarget, 'type' | 'id' | 'storyboardId' | 'resourceType'>,
  storyboardId: string
): boolean {
  const normalizedStoryboardId = storyboardId.trim();
  if (!normalizedStoryboardId) return false;

  if (!hasMatchingStoryboardContext(target, normalizedStoryboardId)) {
    return false;
  }

  return isStoryboardResourceAssociationTarget(target);
}

/** 左侧分镜卡等：分镜图片 + 资源关联任一子级选中 */
export function matchesStoryboardTrackRemoteSelection(
  target: Pick<CollaborationSelectionTarget, 'type' | 'id' | 'storyboardId' | 'resourceType'>,
  storyboardId: string
): boolean {
  return (
    matchesStoryboardImageTrackRemoteSelection(target, storyboardId) ||
    matchesStoryboardResourceTrackRemoteSelection(target, storyboardId) ||
    matchesStoryboardResourceAssociationRemoteSelection(target, storyboardId)
  );
}

/** 资源库卡片：资源本身 + 其下任一标签/素材选中 */
export function matchesResourceElementRemoteSelection(
  target: Pick<CollaborationSelectionTarget, 'type' | 'id'>,
  resourceId: string,
  relatedTagIds: readonly string[]
): boolean {
  const normalizedResourceId = resourceId.trim();
  if (!normalizedResourceId) return false;
  if (target.type === 'resource_element' && target.id === normalizedResourceId) return true;
  if (target.type === 'resource_tag' && relatedTagIds.includes(target.id)) return true;
  if (target.type === 'project_resource' && target.id === normalizedResourceId) return true;
  return false;
}

/** 分镜资源 chip / 预览卡：条目本身 + 同一 tag / 资源元素选中 */
export function matchesStoryboardItemRemoteSelection(
  target: Pick<CollaborationSelectionTarget, 'type' | 'id' | 'storyboardId'>,
  params: {
    itemId: string;
    storyboardId?: string | null;
    resourceId?: string | null;
    tagId?: string | null;
  }
): boolean {
  const normalizedItemId = params.itemId.trim();
  if (!normalizedItemId) return false;
  const normalizedStoryboardId = String(params.storyboardId ?? '').trim();
  const targetStoryboardId = String(target.storyboardId ?? '').trim();
  if (
    normalizedStoryboardId &&
    targetStoryboardId &&
    targetStoryboardId !== normalizedStoryboardId
  ) {
    return false;
  }
  if (target.type === 'storyboard_item' && target.id === normalizedItemId) return true;

  const normalizedTagId = String(params.tagId ?? '').trim();
  if (normalizedTagId && target.type === 'resource_tag' && target.id === normalizedTagId) {
    return true;
  }

  const normalizedResourceId = String(params.resourceId ?? '').trim();
  if (
    normalizedResourceId &&
    target.type === 'resource_element' &&
    target.id === normalizedResourceId
  ) {
    return true;
  }

  return false;
}

/** 看板视频块：轨道元素 + 预览区 videoId 选中 */
export function matchesVideoBoardElementRemoteSelection(
  target: Pick<CollaborationSelectionTarget, 'type' | 'id' | 'storyboardId'>,
  params: { videoId: string; boardElementId: string; storyboardId?: string | null }
): boolean {
  const normalizedVideoId = params.videoId.trim();
  const normalizedBoardElementId = params.boardElementId.trim();
  if (!normalizedVideoId || !normalizedBoardElementId) return false;
  if (target.type === 'storyboard' && target.id === normalizedBoardElementId) return true;
  if (target.type === 'project_resource' && target.id === normalizedVideoId) {
    const normalizedStoryboardId = String(params.storyboardId ?? '').trim();
    const targetStoryboardId = String(target.storyboardId ?? '').trim();
    if (
      normalizedStoryboardId &&
      targetStoryboardId &&
      targetStoryboardId !== normalizedStoryboardId
    ) {
      return false;
    }
    return true;
  }

  return false;
}

export function parseVideoIdFromBoardElementId(elementId: string): string | null {
  const marker = '-element-video-';
  const index = elementId.lastIndexOf(marker);
  if (index === -1) return null;
  const videoId = elementId.slice(index + marker.length).trim();
  return videoId || null;
}

/** 与 usePreviewData.parseVideoTrackSelection 保持一致 */
export function parseVideoTrackSelectionFromBoardItem(selectionId: string): {
  storyboardId: string;
  videoId: string | null;
  isPlaceholder: boolean;
} | null {
  const isPlaceholder = selectionId.includes('-placeholder-');
  const splitKey = isPlaceholder ? '-placeholder-' : '-element-video-';
  const parts = selectionId.split(splitKey);
  if (parts.length < 2) return null;

  const storyboardId = parts[0]?.trim() ?? '';
  const videoId = isPlaceholder ? null : (parts[1]?.trim() ?? '');

  if (!isUuid(storyboardId)) return null;
  if (!isPlaceholder && (!videoId || !isUuid(videoId))) return null;

  return { storyboardId, videoId, isPlaceholder };
}

export function resolveVideoTrackBoardSelectionItem(
  boardItems: readonly string[] | null | undefined
): {
  storyboardId: string;
  videoId: string | null;
  isPlaceholder: boolean;
  boardElementId: string;
} | null {
  if (!boardItems?.length) return null;

  const boardElementId = boardItems.find((id) => id.includes('-video') && !id.startsWith('track-'));
  if (!boardElementId) return null;

  const parsed = parseVideoTrackSelectionFromBoardItem(boardElementId);
  if (!parsed) return null;

  return { ...parsed, boardElementId };
}

function isResourceLibraryImageMode(mode: string): boolean {
  return mode === 'character-image' || mode === 'location-image' || mode === 'prop-image';
}

export function serializeCollaborationSelectionTarget(
  target: CollaborationSelectionTarget | null | undefined
): string {
  if (!target?.id) return '';
  return JSON.stringify({
    type: target.type,
    id: target.id,
    storyboardId: target.storyboardId ?? null,
    resourceType: target.resourceType ?? null,
  });
}

function hasMatchingStoryboardScopeContext(
  userTarget: Pick<CollaborationSelectionTarget, 'storyboardId'>,
  scopeTarget: Pick<CollaborationSelectionTarget, 'storyboardId'>
): boolean {
  const scopeStoryboardId = String(scopeTarget.storyboardId ?? '').trim();
  const userStoryboardId = String(userTarget.storyboardId ?? '').trim();
  if (!scopeStoryboardId || !userStoryboardId) return true;
  return scopeStoryboardId === userStoryboardId;
}

function extractStoryboardIdFromBoardElementId(elementId: string): string | null {
  const normalized = elementId.trim();
  if (!normalized) return null;
  if (normalized.startsWith('resource-')) {
    return normalized.slice('resource-'.length) || null;
  }
  const placeholderIndex = normalized.indexOf('-placeholder-');
  if (placeholderIndex > 0) {
    return normalized.slice(0, placeholderIndex) || null;
  }
  const elementIndex = normalized.indexOf('-element-');
  if (elementIndex > 0) {
    return normalized.slice(0, elementIndex) || null;
  }
  return isUuid(normalized) ? normalized : null;
}

/** 预览监视器：判断远端选中是否属于当前操作实体 */
export function matchesActiveOperatorsPresenceScope(
  userTarget: CollaborationSelectionTarget,
  scopeTarget: CollaborationSelectionTarget
): boolean {
  const normalizedUserTarget = {
    type: userTarget.type,
    id: String(userTarget.id ?? '').trim(),
    storyboardId: userTarget.storyboardId ?? null,
    resourceType: userTarget.resourceType ?? null,
  };
  const normalizedScopeTarget = {
    type: scopeTarget.type,
    id: String(scopeTarget.id ?? '').trim(),
    storyboardId: scopeTarget.storyboardId ?? null,
    resourceType: scopeTarget.resourceType ?? null,
  };

  if (!normalizedUserTarget.id || !normalizedScopeTarget.id) return false;
  if (
    serializeCollaborationSelectionTarget(normalizedUserTarget) ===
    serializeCollaborationSelectionTarget(normalizedScopeTarget)
  ) {
    return true;
  }

  switch (normalizedScopeTarget.type) {
    case 'resource_tag':
      if (
        normalizedUserTarget.type === 'resource_tag' &&
        normalizedUserTarget.id === normalizedScopeTarget.id
      ) {
        return hasMatchingStoryboardScopeContext(normalizedUserTarget, normalizedScopeTarget);
      }
      return false;
    case 'resource_element':
      return matchesResourceElementRemoteSelection(
        normalizedUserTarget,
        normalizedScopeTarget.id,
        []
      );
    case 'project_resource':
      if (
        normalizedUserTarget.type !== 'project_resource' ||
        normalizedUserTarget.id !== normalizedScopeTarget.id
      ) {
        return false;
      }
      if (
        isStoryboardVideoProjectResourceTarget(normalizedScopeTarget) !==
        isStoryboardVideoProjectResourceTarget(normalizedUserTarget)
      ) {
        return false;
      }
      return hasMatchingStoryboardScopeContext(normalizedUserTarget, normalizedScopeTarget);
    case 'storyboard_item':
      return matchesStoryboardItemRemoteSelection(normalizedUserTarget, {
        itemId: normalizedScopeTarget.id,
        storyboardId: normalizedScopeTarget.storyboardId,
      });
    case 'storyboard': {
      const scopeId = normalizedScopeTarget.id;
      if (normalizedUserTarget.type === 'storyboard' && normalizedUserTarget.id === scopeId) {
        return true;
      }

      if (scopeId.startsWith('resource-')) {
        const storyboardId = scopeId.slice('resource-'.length);
        return (
          matchesStoryboardResourceTrackRemoteSelection(normalizedUserTarget, storyboardId) ||
          matchesStoryboardResourceAssociationRemoteSelection(normalizedUserTarget, storyboardId)
        );
      }

      const videoId = parseVideoIdFromBoardElementId(scopeId);
      if (videoId) {
        return matchesVideoBoardElementRemoteSelection(normalizedUserTarget, {
          videoId,
          boardElementId: scopeId,
          storyboardId:
            normalizedScopeTarget.storyboardId ?? extractStoryboardIdFromBoardElementId(scopeId),
        });
      }

      if (scopeId.includes('-placeholder-')) {
        if (scopeId.includes('-placeholder-video')) {
          return matchesVideoTrackPlaceholderRemoteSelection(normalizedUserTarget, {
            boardElementId: scopeId,
            storyboardId:
              normalizedScopeTarget.storyboardId ?? extractStoryboardIdFromBoardElementId(scopeId),
          });
        }
        return normalizedUserTarget.type === 'storyboard' && normalizedUserTarget.id === scopeId;
      }

      if (isStoryboardImageTrackBoardSelectionTarget(normalizedScopeTarget)) {
        return matchesStoryboardImageTrackRemoteSelection(normalizedUserTarget, scopeId);
      }

      return false;
    }
    default:
      return false;
  }
}

/** 由 editorStore + 预览上下文推导 presence 目标（含默认/自动选中） */
export function resolveEditorSelectionPresenceTarget(params: {
  selectedStoryboardId: string | null | undefined;
  selectedResourceId: string | null | undefined;
  selectedTagId: string | null | undefined;
  selectedResourceType: string | null | undefined;
  editorMode: string | null | undefined;
  previewItemId: string | null | undefined;
  resourcesPanelOpen: boolean;
  selectedStoryboardBoardItems?: readonly string[] | null;
}): CollaborationSelectionTarget | null {
  const storyboardId = String(params.selectedStoryboardId ?? '').trim() || null;
  const resourceId = String(params.selectedResourceId ?? '').trim() || null;
  const tagId = String(params.selectedTagId ?? '').trim() || null;
  const previewItemId = String(params.previewItemId ?? '').trim() || null;
  const resourceType = String(params.selectedResourceType ?? '').trim() || null;
  const editorMode = String(params.editorMode ?? '').trim();
  const videoTrack = resolveVideoTrackBoardSelectionItem(params.selectedStoryboardBoardItems);

  if (resourceId && tagId && isResourceLibraryImageMode(editorMode)) {
    return {
      type: 'resource_tag',
      id: tagId,
      resourceType: resourceType || null,
    };
  }

  if (resourceId) {
    return {
      type: 'resource_element',
      id: resourceId,
      resourceType: resourceType || null,
    };
  }

  if (videoTrack?.videoId) {
    const videoStoryboardId = videoTrack.storyboardId || storyboardId;
    if (videoStoryboardId) {
      return resolveStoryboardVideoSelectionPresenceTarget(videoTrack.videoId, {
        storyboardId: videoStoryboardId,
      });
    }
  }

  const nonImageBoardTarget = resolveNonImageBoardTrackPresenceTarget({
    editorMode,
    storyboardId,
    selectedStoryboardBoardItems: params.selectedStoryboardBoardItems,
  });

  if (
    shouldPreferVideoTrackPlaceholderPresenceTarget({
      editorMode,
      previewItemId,
      selectedTagId: tagId,
      storyboardId,
      videoTrack,
    }) &&
    nonImageBoardTarget
  ) {
    return nonImageBoardTarget;
  }

  if (
    editorMode === 'storyboard-video' &&
    previewItemId &&
    storyboardId &&
    !isNonAssetStoryboardVideoPreviewItemId(previewItemId)
  ) {
    return resolveStoryboardVideoSelectionPresenceTarget(previewItemId, { storyboardId });
  }

  if (nonImageBoardTarget) {
    return nonImageBoardTarget;
  }

  const isStoryboardImageEditorMode = editorMode === 'storyboard-image';

  if (isStoryboardImageEditorMode && storyboardId && previewItemId && previewItemId !== tagId) {
    return {
      type: 'project_resource',
      id: previewItemId,
      storyboardId,
    };
  }

  if (storyboardId && params.resourcesPanelOpen) {
    return {
      type: 'storyboard',
      id: buildStoryboardResourceTrackBoardElementId(storyboardId),
    };
  }

  if (isStoryboardImageEditorMode && storyboardId && tagId) {
    return resolveStoryboardSchemeSelectionPresenceTarget(tagId, { storyboardId });
  }

  if (isStoryboardImageEditorMode && storyboardId) {
    return { type: 'storyboard', id: storyboardId };
  }

  return null;
}

export function sortRemoteSelectionUsers(
  users: CollaborationPresenceUser[]
): CollaborationPresenceUser[] {
  return [...users].sort((a, b) => {
    const timeA = Date.parse(a.updatedAt);
    const timeB = Date.parse(b.updatedAt);
    const safeA = Number.isNaN(timeA) ? 0 : timeA;
    const safeB = Number.isNaN(timeB) ? 0 : timeB;
    return safeA - safeB || a.userId.localeCompare(b.userId);
  });
}

export function buildActiveOperatorsPresenceUsers(params: {
  matchedUsers: CollaborationPresenceUser[];
  currentUserId: string | null;
  scopeTarget: CollaborationSelectionTarget | null;
  projectId: string | null;
  displayName?: string | null;
  avatarUrl?: string | null;
}): CollaborationPresenceUser[] {
  const { matchedUsers, currentUserId, scopeTarget, projectId, displayName, avatarUrl } = params;
  if (!scopeTarget?.id || !projectId) return matchedUsers;
  if (!currentUserId) return matchedUsers;
  if (matchedUsers.some((user) => user.userId === currentUserId)) {
    return matchedUsers;
  }

  return sortRemoteSelectionUsers([
    ...matchedUsers,
    {
      userId: currentUserId,
      displayName: displayName ?? null,
      avatarUrl: avatarUrl ?? null,
      projectId,
      pageContext: 'project_page',
      selectedTarget: scopeTarget,
      updatedAt: '0001-01-01T00:00:00.000Z',
    },
  ]);
}
